import React from 'react'
import { Box, type SxProps, type Theme } from '@mui/material'
import { ChevronRightIcon } from '@heroicons/react/24/outline'
import { INTERAX_COLORS } from '../../../../constants/colors'

type ArrowDirection = 'up' | 'down' | 'left' | 'right'

export interface ArrowBoxProps {
  /**
   * Optional accessible label when the arrow is interactive
   */
  ariaLabel?: string
  className?: string
  direction?: ArrowDirection
  disabled?: boolean
  onClick?: () => void
  /**
   * Override styles for the wrapper
   */
  sx?: SxProps<Theme>
  /**
   * Size of the outer box (defaults to 69px)
   */
  size?: number
}

const DIRECTION_ROTATIONS: Record<ArrowDirection, string> = {
  right: 'rotate(0deg)',
  left: 'rotate(180deg)',
  up: 'rotate(-90deg)',
  down: 'rotate(90deg)'
}

/**
 * ArrowBox - Decorative arrow with glassy layers used between onboarding cards.
 */
export const ArrowBox: React.FC<ArrowBoxProps> = ({ ariaLabel, className, direction = 'right', sx, size = 69 }) => {
  const innerSize = Math.round(size * (43 / 69))
  const iconSize = Math.round(size * (19 / 69))

  const isInteractive = false

  return (
    <Box
      className={className}
      aria-label={ariaLabel}
      tabIndex={isInteractive ? 0 : undefined}
      role={isInteractive ? 'button' : undefined}
      onClick={undefined}
      sx={[
        {
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: '24px',
          backgroundColor: 'rgba(120, 144, 156, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: isInteractive ? 'pointer' : 'default',
          transition: 'background-color 0.2s ease, transform 0.2s ease',
          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.25)',
          outline: 'none',
          border: 'none',
          '&:hover': isInteractive
            ? {
                backgroundColor: 'rgba(120, 144, 156, 0.3)'
              }
            : {},
          '&:active': isInteractive
            ? {
                transform: 'scale(0.97)'
              }
            : {},
          '&:focus-visible': isInteractive
            ? {
                boxShadow: `0 0 0 2px ${INTERAX_COLORS.white}40, 0 0 0 4px ${INTERAX_COLORS.sun}60`
              }
            : {}
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : [])
      ]}
    >
      <Box
        sx={{
          width: `${innerSize}px`,
          height: `${innerSize}px`,
          borderRadius: '14px',
          backgroundColor: 'rgba(120, 144, 156, 0.35)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.2s ease',
          ...(isInteractive
            ? {
                '&:hover': {
                  transform: 'scale(1.05)'
                }
              }
            : {})
        }}
      >
        <ChevronRightIcon
          style={{
            width: `${iconSize}px`,
            height: `${iconSize}px`,
            color: 'rgba(255, 255, 255, 0.9)',
            transform: DIRECTION_ROTATIONS[direction]
          }}
          strokeWidth={2.5}
        />
      </Box>
    </Box>
  )
}

export default ArrowBox
